'use strict';

import _ from 'underscore';
import { flow, action, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import events from '@uw-it-sis/lib-react/lib/Events';
import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';

import BookmarkEvents from './BookmarkEvents';

/**
 * Store for bookmarked courses, programs, or credentials.
 */
class BookmarksStore {

    state = States.initial;
    // The data is observable so that updates to the data (from adds or removes) will cause the UI to update.
    bookmarks = {};

    constructor() {
        makeObservable(this, {
            state: observable,
            bookmarks: observable,
            onBookmarkAdded: action,
            onBookmarkDeleted: action
        });

        this.baseUrl = `api/bookmarks`;
        /*
         * This store isn't directly paired with a component so no need to have mount/unmount methods.
         */
        events.listen(BookmarkEvents.added, (bookmark) => this.onBookmarkAdded(bookmark));
        events.listen(BookmarkEvents.deleted, (code) => this.onBookmarkDeleted(code));
    }

    /**
     * Listener for bookmark adds.
     * @param bookmark The new bookmark.
     */
    onBookmarkAdded(bookmark) {
        if (appStore.isDebug()) {
            console.log("BookmarksStore.onBookmarkAdded: Adding bookmark [%s]", JSON.stringify(bookmark));
        }
        this.bookmarks[bookmark.code] = bookmark;
    }

    /**
     * Listener for bookmark deletes.
     * @param code The code (course code or program code) of the bookmark which was removed.
     */
    onBookmarkDeleted(code) {
        if (appStore.isDebug()) {
            console.log(`BookmarksStore.onBookmarkDeleted: Deleting bookmark [${code}]`);
        }
        delete this.bookmarks[code];
    }

    fetchBookmarks() {
        return appStore.getHttpClient().doGet(this.baseUrl, 'getBookmarks');
    }

    /**
     * Load all bookmarks.
     */
    loadBookmarks = flow(function * () {

        this.state = States.pending;

        if (appStore.isDebug()) {
            console.log("BookmarksStore: Beginning data load");
        }

        try {
            let bookmarksArray = yield this.fetchBookmarks();

            let bookmarksObject = {};
            bookmarksArray.map(bookmark => {
                //  Place the bookmark in a map where the key is the code and the value is the JSON response.
                bookmarksObject[bookmark.code] = bookmark;
            });

            this.bookmarks = bookmarksObject;
            if (appStore.isDebug()) {
                console.log("BookmarksStore: Data load complete");
            }

            this.state = States.done;
        } catch(error) {
            this.state = States.error;
        }
    }.bind(this));

    /**
     * Get bookmarks.
     */
    getBookmarks() {
        return this.bookmarks;
    }

    /**
     * Returns true if the given item has been bookmarked. Otherwise, false.
     * @param code Code for the entity.
     */
    isBookmarked(code) {
        let isFound = false;
        if (this.bookmarks[code] ) {
            isFound = true;
        }
        return isFound;
    }

    /**
     * Gets a bookmark by its code.
     */
    getBookmarkByCode(code) {
        return this.bookmarks[code];
    }

    /**
     * Gets the bookmark with the given id.
     */
    getBookmarkById(id) {
        for (let code in this.bookmarks) {
            const bookmark = this.bookmarks[code];
            if (bookmark.id === id) {
                return bookmark;
            }
        }
        return null;
    }

    /**
     * Returns an array of bookmarked course codes
     * @returns {Array}
     */
    getCourseBookmarkCodes() {
        let courseCodes = [];
        // Push the codes into the return array, filtering out any non-course bookmarks.
        _(this.bookmarks).each(function(element){
            if (element.type === "kuali.lu.type.CreditCourse") {
                courseCodes.push(element.code);
            }
        });

        return courseCodes;
    }

    /**
     * Returns an array of bookmarked program codes
     * @returns {Array}
     */
    getProgramBookmarkCodes() {
        let prgCodes = [];
        // Push the codes into the return array, filtering out any course bookmarks.
        _(this.bookmarks).each(function(element){
            if (element.type === " uw.academicplan.program") {
                prgCodes.push(element.code);
            }
        });

        return prgCodes;
    }

}

export default new BookmarksStore();